<template>
  <div v-if="hasError" class="flex flex-wrap gap-1.5">
    <ui-badge v-if="removedBrandFlagIsVisible" variant="destructive">
      {{ tr('Automation Stopped') }} - {{ tr('Brand removed') }}
    </ui-badge>
    <ui-badge v-else-if="inactiveBrandFlagIsVisible" variant="secondary">
      {{ tr('Automation Stopped') }} - {{ tr('Brand inactive') }}
    </ui-badge>
    <template v-else>
      <ui-badge v-if="disconnectedAdAccountFlagIsVisible" variant="destructive">
        {{ tr('Automation Stopped') }} - {{ tr('Ad account disconnected') }}
      </ui-badge>
      <ui-badge v-if="disconnectedSourceProfileFlagIsVisible" variant="destructive">
        {{ tr('Automation Stopped') }} - {{ tr('Datasource disconnected') }}
      </ui-badge>
    </template>

    <template v-if="!disconnectedAdAccountFlagIsVisible && !disconnectedSourceProfileFlagIsVisible">
      <ui-badge v-if="lostAccessAdAccountFlagIsVisible" variant="destructive">
        {{ tr('Automation Stopped') }} - {{ tr('Lost access to ad account') }}
      </ui-badge>
      <ui-badge v-else-if="lostAccessSourceProfilesFlagIsVisible" variant="destructive">
        {{ tr('Automation Stopped') }} - {{ tr('Lost access to brand') }}
      </ui-badge>
      <ui-badge v-else-if="expiredTokenFlagIsVisible" variant="destructive" :title="expiredTokenTitle">
        {{ tr('Automation Stopped') }} - {{ tr('Expired profile connection') }}
      </ui-badge>
      <ui-badge v-else-if="deletedCampaignFlagIsVisible" variant="destructive">
        {{ tr('Automation Stopped') }} - {{ tr('Campaign Deleted on Ads Manager') }}
      </ui-badge>
      <ui-badge v-else-if="inactiveCampaignFlagIsVisible" variant="warning">
        {{ tr('Inactive Campaign') }}
      </ui-badge>
    </template>
  </div>
</template>

<script>
import { CAMPAIGN_STATUS } from '@/consts.js';
import Rule from '@/entities/Rule.js';
import AssetStatusMixin from '@/mixins/AssetStatus.js';

/** Badges for the reasons an automation stopped or cannot run. */
export default {
  name: 'RuleErrorFlags',
  mixins: [ AssetStatusMixin ],
  props: {
    rule: {
      type: Rule,
      required: true,
    },
  },
  computed: {
    hasError() {
      return this.removedBrandFlagIsVisible
        || this.inactiveBrandFlagIsVisible
        || this.disconnectedAdAccountFlagIsVisible
        || this.disconnectedSourceProfileFlagIsVisible
        || this.lostAccessAdAccountFlagIsVisible
        || this.lostAccessSourceProfilesFlagIsVisible
        || this.expiredTokenFlagIsVisible
        || this.deletedCampaignFlagIsVisible
        || this.inactiveCampaignFlagIsVisible;
    },
    removedBrandFlagIsVisible() {
      return !!this.rule.brand?.deleted_at;
    },
    inactiveBrandFlagIsVisible() {
      return this.rule.isBlockedByInactiveBrand;
    },

    disconnectedAdAccountFlagIsVisible() {
      const adAccount = this.$root.currentCompany.ad_accounts.find(item => item.id === this.rule.ad_account.id);
      return !adAccount;
    },
    disconnectedSourceProfileFlagIsVisible() {
      const sourceProfileIds = this.rule.source_profiles.map(item => item.id);
      const sourceProfiles = this.$root.currentCompany.source_profiles.filter(item => sourceProfileIds.includes(item.id));
      return sourceProfiles.length !== sourceProfileIds.length;
    },
    deletedCampaignFlagIsVisible() {
      return this.rule.campaign_status === CAMPAIGN_STATUS.DELETED;
    },
    inactiveCampaignFlagIsVisible() {
      return this.rule.campaign_status === CAMPAIGN_STATUS.INACTIVE && !this.rule.isBlockedByInactiveBrand;
    },
    lostAccessAdAccountFlagIsVisible() {
      return this.rule.hasLostAccessAdAccount;
    },
    lostAccessSourceProfilesFlagIsVisible() {
      return this.rule.hasLostAccessSourceProfiles;
    },
    expiredTokenFlagIsVisible() {
      return this.rule.isBlockedByExpiredToken;
    },
    expiredTokenTitle() {
      return this.expiredConnectionReason(this.rule.expiredTokenConnectedProfile);
    },
  },
};
</script>
